/* Everything the app asks Shopify, through App Bridge's direct API access: the browser calls the Admin GraphQL API as
 * the signed-in staff member (shopify.app.toml: [access.admin] direct_api_mode = "online"). No server, no stored token. */
import { DRAFT_FIELDS, QUOTE_TAG, SENT_TAG, GROUP_PREFIX, fromDraftOrder, customerOf, toDraftOrderInput } from './quote.js';
import { PAINT_HANDLE, containerOz } from './paint.js';

export const API_VERSION = '2026-07';

export class ShopifyError extends Error {}

export async function gql(query, variables = {}) {
  const res = await fetch(`shopify:admin/api/${API_VERSION}/graphql.json`, {
    method: 'POST',
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.errors?.length) {
    const msg = json.errors?.map((e) => e.message).join('; ') || `Shopify answered ${res.status}`;
    throw new ShopifyError(msg);
  }
  return json.data;
}

/* A mutation's payload, or an error carrying Shopify's own words. */
function payload(data, key) {
  const p = data[key];
  const errs = p?.userErrors || [];
  if (errs.length) throw new ShopifyError(errs.map((e) => e.message).join('; '));
  return p;
}

export async function shopInfo() {
  const d = await gql(`{ shop { id name email contactEmail myshopifyDomain primaryDomain { url host } taxesIncluded
    billingAddress { phone address1 city provinceCode zip }
    settings: metafield(namespace: "$app", key: "settings") { value } } }`);
  let settings = {};
  try { settings = d.shop.settings?.value ? JSON.parse(d.shop.settings.value) : {}; } catch { settings = {}; }
  return { ...d.shop, settings };
}

export async function saveSettings(shopId, settings) {
  const d = await gql(
    `mutation($m: [MetafieldsSetInput!]!) { metafieldsSet(metafields: $m) { userErrors { field message } } }`,
    { m: [{ ownerId: shopId, namespace: '$app', key: 'settings', type: 'json', value: JSON.stringify(settings) }] },
  );
  payload(d, 'metafieldsSet');
}

/* ── quotes ── */

export async function listQuotes({ search = '', filter = 'all', after = null } = {}) {
  let q = `tag:${QUOTE_TAG}`;
  if (filter === 'open') q += ' AND status:open';
  if (filter === 'sent') q += ` AND (tag:${SENT_TAG} OR status:invoice_sent) AND NOT status:completed`;
  if (filter === 'paid') q += ' AND status:completed';
  if (search.trim()) q += ` AND (${search.trim()})`;
  const d = await gql(
    `query($q: String!, $after: String) {
      draftOrders(first: 40, after: $after, query: $q, sortKey: UPDATED_AT, reverse: true) {
        pageInfo { hasNextPage endCursor }
        nodes { id name status createdAt updatedAt invoiceSentAt tags email totalQuantityOfLineItems
          customer { id displayName email }
          totalPriceSet { shopMoney { amount } }
          order { id name }
          sent: metafield(namespace: "$app", key: "sent") { value }
          option: metafield(namespace: "$app", key: "option") { value }
          lineItems(first: 3) { nodes { title customAttributes { key value } } } }
      }
    }`,
    { q, after },
  );
  return {
    quotes: d.draftOrders.nodes.map((n) => {
      let sent = null;
      try { sent = n.sent?.value ? JSON.parse(n.sent.value) : null; } catch { sent = null; }
      return {
        id: n.id, name: n.name, status: n.status, tags: n.tags, createdAt: n.createdAt, updatedAt: n.updatedAt,
        sentAt: sent?.at || n.invoiceSentAt, customer: n.customer, email: n.email || n.customer?.email || '',
        total: Number(n.totalPriceSet.shopMoney.amount), items: n.totalQuantityOfLineItems, order: n.order, option: n.option?.value || '',
        preview: n.lineItems.nodes.map((l) => {
          const a = Object.fromEntries(l.customAttributes.map((x) => [x.key, x.value]));
          return a._quote_title || (a['Paint code'] ? `${a.Color || 'Paint'} (${a['Paint code']})` : l.title);
        }),
      };
    }),
    pageInfo: d.draftOrders.pageInfo,
  };
}

export async function getQuote(id) {
  const d = await gql(`query($id: ID!) { draftOrder(id: $id) { ${DRAFT_FIELDS} } }`, { id });
  if (!d.draftOrder) throw new ShopifyError('That quote no longer exists');
  return fromDraftOrder(d.draftOrder);
}

export async function saveQuote(q) {
  const input = toDraftOrderInput(q);
  if (q.id) {
    const d = await gql(
      `mutation($id: ID!, $input: DraftOrderInput!) { draftOrderUpdate(id: $id, input: $input) { draftOrder { ${DRAFT_FIELDS} } userErrors { field message } } }`,
      { id: q.id, input },
    );
    return fromDraftOrder(payload(d, 'draftOrderUpdate').draftOrder);
  }
  const d = await gql(
    `mutation($input: DraftOrderInput!) { draftOrderCreate(input: $input) { draftOrder { ${DRAFT_FIELDS} } userErrors { field message } } }`,
    { input },
  );
  return fromDraftOrder(payload(d, 'draftOrderCreate').draftOrder);
}

/* Shopify's own arithmetic for an unsaved quote: tax by the customer's address, discounts, shipping. */
export async function calculate(q) {
  const input = toDraftOrderInput(q);
  const d = await gql(
    `mutation($input: DraftOrderInput!) { draftOrderCalculate(input: $input) {
      calculatedDraftOrder {
        subtotalPriceSet { shopMoney { amount } } totalDiscountsSet { shopMoney { amount } }
        totalShippingPriceSet { shopMoney { amount } } totalTaxSet { shopMoney { amount } } totalPriceSet { shopMoney { amount } }
        taxLines { title rate } taxesIncluded
      } userErrors { field message } } }`,
    { input },
  );
  const c = payload(d, 'draftOrderCalculate').calculatedDraftOrder;
  const a = (s) => Number(s.shopMoney.amount);
  return {
    subtotal: a(c.subtotalPriceSet), discount: a(c.totalDiscountsSet), shipping: a(c.totalShippingPriceSet),
    tax: a(c.totalTaxSet), total: a(c.totalPriceSet), taxLines: c.taxLines, taxesIncluded: !!c.taxesIncluded,
  };
}

export async function deleteQuote(id) {
  const d = await gql(`mutation($id: ID!) { draftOrderDelete(input: { id: $id }) { deletedId userErrors { field message } } }`, { id });
  payload(d, 'draftOrderDelete');
}

export async function duplicateQuote(id) {
  const d = await gql(`mutation($id: ID!) { draftOrderDuplicate(id: $id) { draftOrder { id } userErrors { field message } } }`, { id });
  return payload(d, 'draftOrderDuplicate').draftOrder.id;
}

/* Another option of the same quote (a gallon kit beside the half gallon): a copy of this draft order, both tagged
   with the group, named Option A / B / C… until renamed. The copy starts unsent. Returns the new draft's id. */
export async function addOption(q) {
  const group = q.group || `${GROUP_PREFIX}${String(q.id).split('/').pop()}`;
  const siblings = q.group ? await listOptions(group) : [];
  if (!q.group) {
    const d = await gql(
      `mutation($id: ID!, $input: DraftOrderInput!) { draftOrderUpdate(id: $id, input: $input) { draftOrder { id } userErrors { field message } } }`,
      { id: q.id, input: { tags: [...new Set([...(q.tags || []), QUOTE_TAG, group])], ...(q.option ? {} : { metafields: [{ namespace: '$app', key: 'option', type: 'single_line_text_field', value: 'Option A' }] }) } },
    );
    payload(d, 'draftOrderUpdate');
  }
  const dup = payload(
    await gql(`mutation($id: ID!) { draftOrderDuplicate(id: $id) { draftOrder { id tags } userErrors { field message } } }`, { id: q.id }),
    'draftOrderDuplicate',
  ).draftOrder;
  const letter = String.fromCharCode(65 + Math.max(1, siblings.length));
  const d = await gql(
    `mutation($id: ID!, $input: DraftOrderInput!, $gone: [MetafieldIdentifierInput!]!) {
      draftOrderUpdate(id: $id, input: $input) { draftOrder { id } userErrors { field message } }
      metafieldsDelete(metafields: $gone) { userErrors { field message } } }`,
    {
      id: dup.id,
      input: {
        tags: [...new Set([...(dup.tags || []).filter((t) => t !== SENT_TAG && !t.startsWith(GROUP_PREFIX)), QUOTE_TAG, group])],
        metafields: [{ namespace: '$app', key: 'option', type: 'single_line_text_field', value: `Option ${letter}` }],
      },
      gone: [{ ownerId: dup.id, namespace: '$app', key: 'sent' }],
    },
  );
  payload(d, 'draftOrderUpdate');
  return dup.id;
}

/* Take an option out: its draft order is deleted (its pay link stops working). When one option is left it stops
   being an option — the group tag and its option name go — and is an ordinary quote again. Returns what is left. */
export async function removeOption(id, group) {
  await deleteQuote(id);
  const left = await listOptions(group);
  if (left.length === 1) {
    const d = await gql(
      `mutation($id: ID!, $t: [String!]!, $gone: [MetafieldIdentifierInput!]!) {
        tagsRemove(id: $id, tags: $t) { userErrors { field message } }
        metafieldsDelete(metafields: $gone) { userErrors { field message } } }`,
      { id: left[0].id, t: [group], gone: [{ ownerId: left[0].id, namespace: '$app', key: 'option' }] },
    );
    payload(d, 'tagsRemove');
  }
  return left;
}

/* The options in a group, oldest first: [{id, name, option, status, total}]. */
export async function listOptions(group) {
  if (!group) return [];
  const d = await gql(
    `query($q: String!) { draftOrders(first: 10, query: $q, sortKey: NUMBER) { nodes { id name status
      totalPriceSet { shopMoney { amount } } option: metafield(namespace: "$app", key: "option") { value } } } }`,
    { q: `tag:${group}` },
  );
  return d.draftOrders.nodes.map((n) => ({ id: n.id, name: n.name, status: n.status, option: n.option?.value || '', total: Number(n.totalPriceSet.shopMoney.amount) }));
}

/* An earlier order, quoted again: Shopify copies its lines (paint properties included) into a new draft. */
export async function quoteFromOrder(orderId) {
  const d = await gql(
    `mutation($id: ID!) { draftOrderCreateFromOrder(orderId: $id) { draftOrder { id tags } userErrors { field message } } }`,
    { id: orderId },
  );
  const draft = payload(d, 'draftOrderCreateFromOrder').draftOrder;
  await gql(`mutation($id: ID!, $t: [String!]!) { tagsAdd(id: $id, tags: $t) { userErrors { message } } }`, { id: draft.id, t: [QUOTE_TAG] });
  return draft.id;
}

/* After an email leaves: the sent tag (searchable in Shopify's own draft list) and when/how/to whom. */
export async function markSent(id, info) {
  await gql(
    `mutation($id: ID!, $t: [String!]!, $m: [MetafieldsSetInput!]!) {
      tagsAdd(id: $id, tags: $t) { userErrors { message } }
      metafieldsSet(metafields: $m) { userErrors { message } } }`,
    { id, t: [SENT_TAG], m: [{ ownerId: id, namespace: '$app', key: 'sent', type: 'json', value: JSON.stringify(info) }] },
  );
}

/* Without Gmail connected: Shopify's own invoice email (Settings ▸ Notifications ▸ Draft order invoice). */
export async function sendShopifyInvoice(id, { to, subject, customMessage }) {
  const d = await gql(
    `mutation($id: ID!, $email: EmailInput) { draftOrderInvoiceSend(id: $id, email: $email) { draftOrder { id invoiceSentAt } userErrors { field message } } }`,
    { id, email: { to, subject, customMessage } },
  );
  return payload(d, 'draftOrderInvoiceSend').draftOrder;
}

/* ── customers ── */

const CUSTOMER_FIELDS = 'id displayName firstName lastName email phone defaultAddress { company city provinceCode }';

export async function searchCustomers(text) {
  const t = text.trim();
  if (!t) return [];
  const d = await gql(`query($q: String!) { customers(first: 8, query: $q) { nodes { ${CUSTOMER_FIELDS} } } }`, { q: t });
  return d.customers.nodes.map(customerOf);
}

export async function getCustomer(id) {
  const d = await gql(`query($id: ID!) { customer(id: $id) { ${CUSTOMER_FIELDS} } }`, { id });
  return customerOf(d.customer);
}

export async function createCustomer({ firstName, lastName, email, phone, company }) {
  const input = { firstName, lastName, email: email || null, phone: phone || null, tags: ['quote-customer'] };
  const d = await gql(
    `mutation($input: CustomerInput!) { customerCreate(input: $input) { customer { ${CUSTOMER_FIELDS} } userErrors { field message } } }`,
    { input },
  );
  const c = payload(d, 'customerCreate').customer;
  if (company) {
    const a = await gql(
      `mutation($id: ID!, $address: MailingAddressInput!) { customerAddressCreate(customerId: $id, address: $address, setAsDefault: true) { address { id } userErrors { field message } } }`,
      { id: c.id, address: { company, firstName, lastName, countryCode: 'US' } },
    ).catch(() => null);
    if (a && !a.customerAddressCreate?.userErrors?.length) c.defaultAddress = { ...(c.defaultAddress || {}), company };
  }
  return customerOf(c);
}

/* ── products ── */

export async function paintProduct() {
  const d = await gql(
    `query($h: String!) { productByHandle(handle: $h) { id title
      variants(first: 100) { nodes { id title sku price availableForSale selectedOptions { name value } } } } }`,
    { h: PAINT_HANDLE },
  );
  if (!d.productByHandle) throw new ShopifyError(`The paint product (${PAINT_HANDLE}) was not found`);
  return { ...d.productByHandle, variants: d.productByHandle.variants.nodes };
}

/* The paint finder's add-ons (activator, reducer, clears, kit) checked against the live products: only active,
   sellable variants, at today's price. finder = {addons: {key: {variants: [{id}]}}, kit: {id}} from the live page. */
/* Add-ons the finder does not carry but the counter uses: High Teck 1020-4 urethane catalyst, the other quart of
   activator for 2K single stage (SKU HIT.1020-4.QT). */
export const EXTRA_ADDONS = { act_ht: 51599247868225 };

export async function finderAddons(finder) {
  const keys = Object.entries(finder?.addons || {}).map(([k, a]) => [k, a?.variants?.[0]?.id]).filter(([, id]) => id);
  for (const [k, id] of Object.entries(EXTRA_ADDONS)) if (!keys.some(([key]) => key === k)) keys.push([k, id]);
  if (finder?.kit?.id) keys.push(['kit', finder.kit.id]);
  if (!keys.length) return {};
  const d = await gql(
    `query($ids: [ID!]!) { nodes(ids: $ids) { ... on ProductVariant { id sku title price availableForSale
      image { url(transform: { maxWidth: 120, maxHeight: 120 }) }
      product { title status featuredMedia { preview { image { url(transform: { maxWidth: 120, maxHeight: 120 }) } } } } } } }`,
    { ids: keys.map(([, id]) => `gid://shopify/ProductVariant/${id}`) },
  );
  const out = {};
  d.nodes.forEach((v, i) => {
    if (!v || v.product?.status !== 'ACTIVE' || v.availableForSale === false) return;
    out[keys[i][0]] = {
      variantId: v.id, title: v.product.title, sku: v.sku || '', price: Number(v.price),
      oz: containerOz(v.sku, `${v.product.title} ${v.title}`),
      image: v.image?.url || v.product.featuredMedia?.preview?.image?.url || '',
    };
  });
  return out;
}

/* The store's catalogue, searched the way the admin's own product search does (title, SKU, vendor…). */
export async function searchProducts(text) {
  const t = text.trim();
  if (t.length < 2) return [];
  const d = await gql(
    `query($q: String!) { productVariants(first: 25, query: $q) { nodes {
      id title sku price inventoryQuantity
      image { url(transform: { maxWidth: 120, maxHeight: 120 }) }
      product { title vendor status featuredMedia { preview { image { url(transform: { maxWidth: 120, maxHeight: 120 }) } } } } } } }`,
    { q: t },
  );
  return d.productVariants.nodes
    .filter((v) => v.product.status !== 'ARCHIVED')
    .map((v) => ({
      variantId: v.id,
      title: v.product.title,
      variantTitle: v.title === 'Default Title' ? '' : v.title,
      sku: v.sku || '',
      price: Number(v.price),
      stock: v.inventoryQuantity,
      vendor: v.product.vendor,
      draft: v.product.status === 'DRAFT',
      image: v.image?.url || v.product.featuredMedia?.preview?.image?.url || '',
    }));
}

export async function recentOrders(customerId) {
  const d = await gql(
    `query($id: ID!) { customer(id: $id) { orders(first: 5, sortKey: CREATED_AT, reverse: true) { nodes { id name createdAt totalPriceSet { shopMoney { amount } } } } } }`,
    { id: customerId },
  );
  return d.customer?.orders?.nodes || [];
}
